/**
 * Uma falha técnica localizada (E140.F3, ADR-0096 §2.6): onde está, o que faltou, quantas vezes o
 * sistema já leu de novo e as duas saídas da equipe — tentar a leitura de novo (a ação principal,
 * quando a peça se retoma) ou registrar a limitação com justificativa. O arquivo recebido segue
 * preservado, e nada é pedido ao banco.
 */
import { useId, useState } from 'react';

import { Button, Field, Textarea } from '@/components/ui';
import {
  JUSTIFICATIVA_MINIMA,
  paginaDaPendencia,
  podeTentarDeNovo,
  type AcaoDaRetomada,
  type PendenciaDaLeituraData,
  type PedidoDeRetomadaData,
} from '@/lib/conferencia_da_leitura';
import type { PaginaNaFonte } from '@/lib/ficha_da_leitura';
import type { ResultadoDoPedido } from '@/lib/useConferenciaDaLeitura';

import { ondeEsta, preencher, t, textoDaPendencia } from './textos';

type Retorno = { tom: 'erro'; texto: string } | null;

export interface PendenciaTecnicaProps {
  pendencia: PendenciaDaLeituraData;
  onVerNaFonte: (alvo: PaginaNaFonte) => void;
  pedir: (pedido: PedidoDeRetomadaData) => Promise<ResultadoDoPedido>;
  /** O pedido atendido: o painel avisa, porque a pendência resolvida sai da lista. */
  onFeito: (texto: string) => void;
  /** E205.F4.C02: só consulta ("Informações da leitura"): sem pedido, só "Ver na fonte". */
  consulta?: boolean;
  /** E221.F2: a conferência ainda tem página ou documento sem leitura (`haPaginaEmAberto`). */
  paginasEmAberto?: boolean;
}

function textoDaRecusa(resultado: ResultadoDoPedido): string {
  if (resultado.ok) return '';
  return preencher(t.recusa[resultado.recusa], { n: JUSTIFICATIVA_MINIMA });
}

/** "Tentar a leitura de novo" e "Registrar limitação": os pedidos da equipe (fora da consulta). */
function PedidosDaEquipe({
  lerDeNovo,
  limitando,
  enviando,
  onLerDeNovo,
  onLimitar,
}: {
  lerDeNovo: boolean;
  limitando: boolean;
  enviando: boolean;
  onLerDeNovo: () => void;
  onLimitar: () => void;
}): JSX.Element {
  return (
    <>
      {lerDeNovo ? (
        <Button type="button" size="sm" disabled={enviando} onClick={onLerDeNovo}>
          {t.lerDeNovo}
        </Button>
      ) : null}
      {!limitando ? (
        <Button
          type="button"
          size="sm"
          variant={lerDeNovo ? 'secondary' : 'primary'}
          disabled={enviando}
          onClick={onLimitar}
        >
          {t.registrarLimitacao}
        </Button>
      ) : null}
    </>
  );
}

export function PendenciaTecnica({
  pendencia,
  onVerNaFonte,
  pedir,
  onFeito,
  consulta = false,
  paginasEmAberto = true,
}: PendenciaTecnicaProps): JSX.Element {
  const [limitando, setLimitando] = useState(false);
  const [justificativa, setJustificativa] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [retorno, setRetorno] = useState<Retorno>(null);
  const idDoTitulo = useId();
  const fonte = paginaDaPendencia(pendencia);
  const lerDeNovo = podeTentarDeNovo(pendencia);

  async function enviar(acao: AcaoDaRetomada): Promise<void> {
    setEnviando(true);
    setRetorno(null);
    const resultado = await pedir({
      chave: pendencia.chave,
      acao,
      justificativa: acao === 'limitacao' ? justificativa.trim() : null,
    });
    setEnviando(false);
    if (resultado.ok) {
      setLimitando(false);
      setRetorno(null);
      onFeito(t.pedidoFeito[acao]);
      return;
    }
    setRetorno({ tom: 'erro', texto: textoDaRecusa(resultado) });
  }

  const curta = justificativa.trim().length < JUSTIFICATIVA_MINIMA;
  return (
    <li
      className="space-y-sc-2 border border-border p-sc-4"
      aria-labelledby={idDoTitulo}
      data-testid="pendencia-tecnica"
      data-tipo={pendencia.tipo}
    >
      <p id={idDoTitulo} className="text-body-md font-semibold text-fg">
        {ondeEsta(pendencia)}
      </p>
      <p className="text-body-sm text-fg">{textoDaPendencia(pendencia, paginasEmAberto)}</p>
      <p className="text-caption text-fg-muted">
        {pendencia.recuperavel ? t.emRecuperacao : t.aguardaEquipe}{' '}
        {preencher(t.tentativas, { n: pendencia.tentativas })}
      </p>
      <div className="flex flex-wrap gap-sc-2">
        {consulta ? null : (
          <PedidosDaEquipe
            lerDeNovo={lerDeNovo}
            limitando={limitando}
            enviando={enviando}
            onLerDeNovo={() => void enviar('tentativa')}
            onLimitar={() => setLimitando(true)}
          />
        )}
        {fonte !== null ? (
          <Button type="button" size="sm" variant="ghost" onClick={() => onVerNaFonte(fonte)}>
            {t.verNaFonte}
          </Button>
        ) : null}
      </div>
      {limitando ? (
        <form
          className="space-y-sc-2"
          onSubmit={(evento) => {
            evento.preventDefault();
            void enviar('limitacao');
          }}
        >
          <Field
            label={t.justificativaRotulo}
            description={preencher(t.justificativaAjuda, { n: JUSTIFICATIVA_MINIMA })}
            required
          >
            <Textarea
              value={justificativa}
              onChange={(evento) => setJustificativa(evento.target.value)}
              rows={3}
              maxLength={500}
              data-testid="pendencia-justificativa"
            />
          </Field>
          <div className="flex gap-sc-2">
            <Button type="submit" size="sm" disabled={enviando || curta}>
              {enviando ? t.enviando : t.salvarLimitacao}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setLimitando(false)}>
              {t.cancelar}
            </Button>
          </div>
        </form>
      ) : null}
      {retorno !== null ? (
        <p role="alert" className="text-body-sm text-danger-700" data-testid="pendencia-retorno">
          {retorno.texto}
        </p>
      ) : null}
    </li>
  );
}
